import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { animated, useSpring } from "@react-spring/web";

// Type fix for animated.div with React 19 - animated components from react-spring
// have stricter types that don't include children/className by default
const AnimatedDiv = animated("div");
import cx from "classnames";

import Text, { TextSize } from "../Text";
import Divider from "../Divider";

import { ChevronIcon } from "../Icons";
import type { ContentPadding, Variant } from "./styles";
import * as s from "./styles";
import styles from "./styles.module.css";

export interface Props {
  children?: ReactNode;
  defaultOpen?: boolean;
  icon?: ReactNode;
  iconOpen?: ReactNode;
  onItemClick?: (isOpen: boolean, title?: string) => void;
  title?: string;
  headerComponent?: ReactNode;
  variant?: Variant;
  disabled?: boolean;
  disabledIcon?: ReactNode;
  chevronIconPosition?: "left" | "right";
  fullWidth?: boolean;
  chevronColor?: string;
  chevronOpenColor?: string;
  chevronVerticalAlign?: "top" | "center";
  padding?: "none" | "sm" | "md" | "lg";
  contentPadding?: ContentPadding;
  chevronDirection?: "vertical" | "horizontal";
}

export default function Disclosure({
  children,
  defaultOpen = false,
  icon,
  iconOpen,
  chevronVerticalAlign = "center",
  onItemClick,
  title,
  headerComponent,
  variant = "default",
  disabled = false,
  padding = "md",
  disabledIcon,
  chevronIconPosition = "right",
  fullWidth,
  contentPadding,
  chevronColor,
  chevronOpenColor,
  chevronDirection = "vertical",
}: Readonly<Props>): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [animationStart, setAnimationStart] = useState(false);
  const [showBorderRadius, setShowBorderRadius] = useState(!defaultOpen);
  const [style, animate] = useSpring(() => ({
    height: "0px",
    opacity: 0,
    onStart: () => {
      setAnimationStart(true);
    },
    onRest: () => {
      setAnimationStart(false);
    },
  }));

  // ResizeObserver to watch content changes
  useEffect(() => {
    const handleResize = () => {
      if (!isOpen || !ref.current) return;
      animate.set({
        height: `${ref.current.scrollHeight}px`,
      });
    };

    const observer = new ResizeObserver(handleResize);
    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [animate, isOpen]);

  useEffect(() => {
    const onAnimationEnd = !animationStart;
    if (onAnimationEnd && !isOpen) {
      setShowBorderRadius(true);
    } else {
      setShowBorderRadius(false);
    }
  }, [animationStart]);

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  useEffect(() => {
    animate.start({
      height: isOpen && ref.current ? `${ref.current.scrollHeight}px` : "0px",
      opacity: isOpen ? 1 : 0,
    });
  }, [animate, isOpen]);

  const handleClickButton = () => {
    if (disabled) {
      return;
    }

    const newValue = !isOpen;
    onItemClick?.(newValue, title);
    setIsOpen(newValue);
  };

  let headerTextSize: TextSize = "base";
  if (variant === "tertiary") {
    headerTextSize = "xl";
  } else if (variant === "secondary") {
    headerTextSize = "2xl";
  }

  return (
    <div className={s.getDisclosureWrapper(variant)}>
      <button
        style={{ transition: "border-radius 0.2s ease" }}
        className={`${s.getButtonWrapper(
          variant,
          isOpen,
          disabled ? (!children ? "cursor-auto" : "cursor-not-allowed") : "",
          chevronIconPosition,
          chevronVerticalAlign,
          fullWidth,
          padding,
          showBorderRadius,
        )} ${variant === "glass" && `${styles.box} ${isOpen ? styles.tealBorder : styles.purpleBorder}`}`}
        onClick={handleClickButton}
      >
        {chevronIconPosition === "left" &&
          getIcon(
            disabled,
            disabledIcon,
            isOpen,
            icon,
            iconOpen,
            chevronColor,
            chevronOpenColor,
            chevronDirection,
          )}
        {headerComponent ?? (
          <span className={s.header}>
            {icon && <span className="mr-2">{icon}</span>}
            <Text size={headerTextSize}>
              <strong>{title}</strong>
            </Text>
          </span>
        )}
        {chevronIconPosition === "right" &&
          getIcon(
            disabled,
            disabledIcon,
            isOpen,
            icon,
            iconOpen,
            chevronColor,
            chevronOpenColor,
            chevronDirection,
          )}
      </button>
      <div className={s.getContentWrapper(variant, isOpen || animationStart)}>
        <AnimatedDiv
          className={cx("w-full rounded-b-lg", {
            hidden: !isOpen && !animationStart,
          })}
          style={style}
        >
          <div
            ref={ref}
            className={s.getContentInnerWrapper(
              variant,
              fullWidth,
              contentPadding,
            )}
          >
            {variant === "secondary" ? <Divider margin="none" /> : null}
            {variant === "darkglass" ? (
              <Divider margin="none" variant="accent" />
            ) : null}
            <div className={s.getContentChildrenWrapper(variant)}>
              {children}
            </div>
          </div>
        </AnimatedDiv>
      </div>
    </div>
  );
}

function getIcon(
  disabled: boolean,
  disabledIcon: ReactNode | undefined,
  isOpen: boolean,
  icon?: ReactNode,
  iconOpen?: ReactNode,
  chevronColor?: string,
  chevronOpenColor?: string,
  chevronDirection: "vertical" | "horizontal" = "vertical",
) {
  if (disabled && disabledIcon !== undefined) {
    return disabledIcon;
  }

  if (icon || iconOpen) {
    return isOpen ? (iconOpen ?? icon) : icon;
  }

  const activeChevronColor =
    isOpen && chevronOpenColor ? chevronOpenColor : chevronColor;

  if (chevronDirection === "vertical") {
    return (
      <ChevronIcon
        direction="down"
        className={s.getChevronStyle(isOpen)}
        color={activeChevronColor}
      />
    );
  }

  return (
    <ChevronIcon
      direction={isOpen ? "down" : "right"}
      color={activeChevronColor}
    />
  );
}
